import { useEffect, useState } from "react";

export interface IModalDimensions {
  modalTop: string;
  maxHeight: string;
}

export interface IModalSafeArea {
  verticalMarginTop?: number;
  verticalMarginBottom?: number;
}

const DEFAULT_MARGIN = 5;

/* eslint-disable import/no-anonymous-default-export */
export default (modalRef: React.RefObject<HTMLDivElement>, safeArea?: IModalSafeArea): IModalDimensions => {
  const [modalTop, setModalTop] = useState("50vh");
  const [maxHeight, setMaxHeight] = useState("90vh");
  const marginTop = safeArea?.verticalMarginTop ?? DEFAULT_MARGIN;
  const marginBottom = safeArea?.verticalMarginBottom ?? DEFAULT_MARGIN;

  // Standalone (bez iframe): ustaw maxHeight z marginesami
  useEffect(() => {
    if (window.self === window.top && (marginTop > DEFAULT_MARGIN || marginBottom > DEFAULT_MARGIN)) {
      setMaxHeight(`calc(100vh - ${marginTop + marginBottom}px)`);
    }
  }, [marginTop, marginBottom]);

  // Nasłuchujemy komunikatów z rodzica
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const raw = typeof event.data === "string" ? event.data : "";
      if (raw.startsWith("parentScrollInfo::")) {
        const parts = raw.split("::");
        if (parts.length >= 5) {
          const parentScrollValue = parseFloat(parts[1]);
          const viewportHeight = parseFloat(parts[2]);
          const iframeTopPosition = parseFloat(parts[3]);
          const iframeBottomPosition = parseFloat(parts[4]);
          if (
            !isNaN(parentScrollValue) &&
            !isNaN(viewportHeight) &&
            !isNaN(iframeTopPosition) &&
            !isNaN(iframeBottomPosition)
          ) {
            updateModalPosition(parentScrollValue, viewportHeight, iframeTopPosition, iframeBottomPosition);
          }
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
    //eslint-disable-next-line
  }, []);

  const updateModalPosition = (
    parentScroll: number,
    viewportHeight: number,
    iframeTopPosition: number,
    iframeBottomPosition: number,
  ) => {
    if (window.self !== window.top) {
      // Jesteśmy w iframe – modal ma zajmować pełną dostępną wysokość viewportu rodzica
      const iframeHeight = iframeBottomPosition - iframeTopPosition;

      // maxHeight: ZAWSZE viewportHeight - marginesy. NIE redukować na podstawie overlap –
      // circular dependency: modalHeight zależy od maxHeight, overlap zależy od modalHeight.
      const availableMaxHeight = viewportHeight - marginTop - marginBottom;
      const finalMaxHeight = Math.max(availableMaxHeight, 200);

      // Środek modala w viewportcie rodzica → pozycja w współrzędnych iframe
      let candidate = parentScroll + viewportHeight / 2 - iframeTopPosition;

      // Granice: używamy finalMaxHeight (docelowa wysokość), NIE modalHeight –
      // unikamy circular dependency przy pierwszym renderze (90vh → 1171px → zły overlap).
      const estimatedModalHeight = modalRef?.current?.offsetHeight || finalMaxHeight;
      const heightForBounds = Math.min(estimatedModalHeight, finalMaxHeight);
      const lowerBound = heightForBounds / 2;
      const upperBound = Math.max(lowerBound, iframeHeight - heightForBounds / 2);
      candidate = Math.min(Math.max(candidate, lowerBound), upperBound);

      if (modalRef?.current) {
        setModalTop(`${candidate}px`);
        setMaxHeight(`${finalMaxHeight}px`);
      } else {
        const correctedTop = parentScroll + viewportHeight / 2 - iframeTopPosition;
        setModalTop(`${correctedTop}px`);
        setMaxHeight(`${finalMaxHeight}px`);
      }
    } else {
      // Nie jesteśmy w iframe
      setModalTop("50vh");
      setMaxHeight("90vh");
    }
  };

  return { modalTop, maxHeight };
};